import { Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BaseModalComponent, EnumValues } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { Roll } from '@app/models/knitting/roll';
import { SecondQualityRollCommand } from '@app/models/production/commands/second-quality-roll-command';
import { FinishingRoute } from '@app/models/production/finishing-route';
import { FinishingRouteProxyService } from '@app/proxies/production/finishing-route.proxy.service';
import { RollLabelProxyService } from '@app/proxies/production/roll-label.proxy.service';
import { EnumService } from '@app/services/enum.service';

export interface SecondQualityRollResult {
  newRoll: Roll;
}

interface RollQualityOption {
  label: string;
  value: number;
}

@Component({
  selector: 'app-second-quality-roll-modal',
  templateUrl: './second-quality-roll-modal.component.html',
  styleUrl: './second-quality-roll-modal.component.scss',
})
export class SecondQualityRollModalComponent extends BaseModalComponent<SecondQualityRollResult | null> implements OnInit {
  @Input() finishingRoute!: FinishingRoute;
  @Input() secondQualityRolls: Roll[] = [];

  private readonly fb = inject(FormBuilder);
  private readonly enumService = inject(EnumService);
  private readonly finishingRouteProxyService = inject(FinishingRouteProxyService);
  private readonly rollLabelProxyService = inject(RollLabelProxyService);
  private readonly translate = inject(TranslateService);

  form!: FormGroup;
  fabricCodeOptions = signal<string[]>([]);
  isSubmitting = signal(false);

  rollQualityOptions: RollQualityOption[] = [];
  rollQualityValues: EnumValues = {};

  totalSecondQualityWeight = computed(() => {
    return this.secondQualityRolls.reduce((sum, roll) => {
      const weight = roll.finalWeight?.weightInLbs ?? roll.grossQuantity ?? roll.netQuantity ?? 0;
      return sum + weight;
    }, 0);
  });

  ngOnInit(): void {
    this.initializeRollQualityOptions();
    this.initializeFabricCodeOptions();
    this.initializeForm();
  }

  private initializeRollQualityOptions(): void {
    const rollQualityEnum = this.enumService.getEnum('RollQuality', 'productionEnums')?.values ?? {};
    // Store all quality values for display in the table
    this.rollQualityValues = rollQualityEnum;
    // Only show quality options 1 and 2 in the dropdown
    this.rollQualityOptions = Object.entries(rollQualityEnum)
      .filter(([key]) => {
        const value = parseInt(key, 10);
        return value === 1 || value === 2;
      })
      .map(([key, label]) => ({
        label: label as string,
        value: parseInt(key, 10),
      }));
  }

  private initializeFabricCodeOptions(): void {
    const fabricCodes = this.finishingRoute?.fabricCodes ?? [];
    this.fabricCodeOptions.set(fabricCodes);
  }

  private initializeForm(): void {
    const defaultFabricCode = this.fabricCodeOptions().length === 1 ? this.fabricCodeOptions()[0] : null;

    this.form = this.fb.group({
      fabricCode: [defaultFabricCode, Validators.required],
      quality: [null, Validators.required],
      weightInLbs: [null, [Validators.required, Validators.min(0.01)]],
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (!this.finishingRoute?.id) {
      GlobalMessageUtil.error(
        this.translate.instant('Finishing_Route_Detail_Error'),
        this.translate.instant('Finishing_Route_Detail_Route_ID_Not_Found'),
      );
      return;
    }

    const formValue = this.form.value;
    const command: SecondQualityRollCommand = {
      fabricCode: formValue.fabricCode,
      quality: formValue.quality,
      weightInLbs: formValue.weightInLbs,
      stationAssetId: 'WST-X',
    };

    this.isSubmitting.set(true);

    this.finishingRouteProxyService.addSecondQualityRolls(this.finishingRoute.id, command).subscribe({
      next: (response) => {
        this.isSubmitting.set(false);
        if (response.success && response.data) {
          GlobalMessageUtil.success(
            this.translate.instant('Finishing_Route_Detail_Success'),
            this.translate.instant('Second_Quality_Roll_Added_Successfully'),
          );
          this.close({ action: 'save', data: { newRoll: response.data } });
        }
      },
      error: (error) => {
        this.isSubmitting.set(false);
        console.error('Error adding second quality roll:', error);
      },
    });
  }

  onClose(): void {
    this.close({ action: 'close', data: null });
  }

  printFinalLabel(rollNumber: string): void {
    this.rollLabelProxyService.getFinalLabelByRollNumber(rollNumber).subscribe({
      next: (response) => {
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.left = '-9999px';
        iframe.style.top = '-9999px';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.visibility = 'hidden';

        document.body.appendChild(iframe);

        iframe.onload = () => {
          try {
            setTimeout(() => {
              if (iframe.contentWindow) {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
              }
            }, 500);
          } catch (e) {
            console.error('Error printing PDF:', e);
            window.open(url, '_blank');
            setTimeout(() => {
              document.body.removeChild(iframe);
              URL.revokeObjectURL(url);
            }, 1000);
          }
        };

        iframe.src = url;
      },
      error: (error) => {
        console.error('Error generating final label:', error);
      },
    });
  }
}
